import { Anchor } from 'components/atoms';
import { PlusInformation } from 'components/profiles';
import { useRouter } from 'next/router';
import React from 'react';
import { ISO8601Date, Referenz, ReferenzType, PremiumInfoDto } from 'types';
import { FeedItem, PersonenFeedItemType, UnternehmensFeedItemType } from 'types/änderungen';
import { useTranslation } from 'utils/hooks';
import { TranslationKey } from 'utils/hooks/useTranslation';

import css from './ZeitlicherVerlauf.module.scss';


type ZeitlicherVerlaufProps =
  | {
    variant: 'unternehmen';
    items: Record<ISO8601Date, FeedItem<UnternehmensFeedItemType>[]>;
    indices: number[];
    id: string;
    plusInhalte: PremiumInfoDto | null;
  }
  | {
    variant: 'person';
    items: Record<ISO8601Date, FeedItem<PersonenFeedItemType>[]>;
    indices: number[];
    id: string;
    plusInhalte: PremiumInfoDto | null;
  };

const ZeitlicherVerlauf: React.FC<ZeitlicherVerlaufProps> = (props) => 
{
  const { items, variant, indices, plusInhalte } = props;
  const { t, r } = useTranslation(['änderungen', 'marken']);
  const token = useRouter().query.token;

  const getLinkFromReferenz = (ref: Referenz) => 
  {
    const id = ref?.id ?? (ref as any)?._id;
    if (!id || ref.type === 'AusländischesUnternehmen') 
    {
      return '';
    }
    const prefix = [ReferenzType.Unternehmen, ReferenzType.AusländischesUnternehmen].includes(ref.type) ? 'u' : 'p';
    return `/${prefix}/${id}`;
  };

  const translateUnternehmensItem = (item: FeedItem<UnternehmensFeedItemType>) => 
  {
    const { type, payload } = item;
    let preProcessedPayload = payload;

    switch (type) 
    {
      case UnternehmensFeedItemType.RechtsformChanged: {
        preProcessedPayload = t(`rechtsformen:list.${payload}` as TranslationKey);
        break;
      }

      case UnternehmensFeedItemType.AufsichtsratAdded:
      case UnternehmensFeedItemType.AufsichtsratChanged:
      case UnternehmensFeedItemType.AufsichtsratDeleted:
      case UnternehmensFeedItemType.FunktionAdded:
      case UnternehmensFeedItemType.FunktionChanged:
      case UnternehmensFeedItemType.FunktionDeleted: {
        const art = t(`funktionen:list.${payload.art}` as TranslationKey);
        const zeichnungsberechtigung = payload.zeichnungsberechtigung !== null && payload.zeichnungsberechtigung.length > 0
          ? t('änderungen:mitZeichnungsberechtigung', { value: payload.zeichnungsberechtigung })
          : '';

        preProcessedPayload = { ...preProcessedPayload, art, zeichnungsberechtigung };
        break;
      }

      case UnternehmensFeedItemType.GesellschafterAdded:
      case UnternehmensFeedItemType.GesellschafterChanged:
      case UnternehmensFeedItemType.GesellschafterDeleted: {
        const beteiligung = payload.beteiligung !== null
          ? t('änderungen:inHöheVon', { value: payload.beteiligung })
          : '';

        preProcessedPayload = { ...preProcessedPayload, beteiligung };
        break;
      }

      case UnternehmensFeedItemType.AdresseChanged: {
        if (preProcessedPayload === null || preProcessedPayload === undefined) 
        {
          preProcessedPayload = { full: t('änderungen:adresseUnbekannt') };
        }
        break;
      }

      case UnternehmensFeedItemType.KapitalChanged: {
        preProcessedPayload = t('formats:money', { value: payload });
        break;
      }
      case UnternehmensFeedItemType.UnternehmenswertanteilChanged: {
        preProcessedPayload = t('formats:money', { value: payload });
        break;
      }

      case UnternehmensFeedItemType.GewerbeberechtigungAdded: {
        const geschäftsführer = payload.ref !== null
          ? t('änderungen:gewerberechtlicherGF', { value: payload.ref.name })
          : '';

        preProcessedPayload = { ...preProcessedPayload, geschäftsführer };
        break;
      }

      case UnternehmensFeedItemType.NationaleMarkeAdded: {
        if (payload.name !== '') 
        {
          preProcessedPayload = t('änderungen:markeMitTitelAdded', { name: payload.name });
        }
        else 
        {
          preProcessedPayload = t(`marken:list.${payload.art}`);
        }

        break;
      }
      case UnternehmensFeedItemType.NationaleMarkeChanged: {
        if (payload.name !== '') 
        {
          preProcessedPayload = t('änderungen:markeMitTitelChanged', { name: payload.name });
        }
        else 
        {
          const translatedArt = t(`marken:list.${payload.art}`);
          preProcessedPayload = t('änderungen:markeOhneTitelChanged', { art: translatedArt });
        }

        break;
      }

    }


    const baseProfileLink = `/${props.variant === 'unternehmen' ? 'u' : 'p'}/${props.id}/plus` + (token ? `?token=${token}` : '');
    const richTextComponents = {
      referenz: <Anchor href={getLinkFromReferenz(payload?.ref)}/>,
      otsSection: <Anchor href={`${baseProfileLink}#aussendungen`}/>,
      rechtstatsachenSection: <Anchor href={`${baseProfileLink}#rechtstatsachen`}/>,
      gisaSection: <Anchor href={`${baseProfileLink}#gewerbeberechtigungen`}/>,
      dokumenteSection: <Anchor href={`${baseProfileLink}#dokumente`}/>,
      markenSection: <Anchor href={`${baseProfileLink}#marken`}/>,
      patenteSection: <Anchor href={`${baseProfileLink}#patente`}/>,
      insolvenzSection: <Anchor href={`${baseProfileLink}#insolvenz`}/>,
    };

    return r(`änderungen:liste.unternehmen.${type}`, richTextComponents, { payload: preProcessedPayload });
  };

  const translatePersonenItem = (item: FeedItem<PersonenFeedItemType>) => 
  {
    const { type, payload } = item;
    let preProcessedPayload = payload;

    switch (type) 
    {
      case PersonenFeedItemType.FunktionAdded:
      case PersonenFeedItemType.FunktionChanged:
      case PersonenFeedItemType.FunktionDeleted: {
        const art = t(`funktionen:list.${payload.art}` as TranslationKey);
        const zeichnungsberechtigung = payload.zeichnungsberechtigung !== null && payload.zeichnungsberechtigung.length > 0
          ? t('änderungen:mitZeichnungsberechtigung', { value: payload.zeichnungsberechtigung })
          : '';

        preProcessedPayload = { ...preProcessedPayload, art, zeichnungsberechtigung };
        break;
      }

      case PersonenFeedItemType.GesellschafterAdded:
      case PersonenFeedItemType.GesellschafterChanged:
      case PersonenFeedItemType.GesellschafterDeleted: {
        const beteiligung = payload.beteiligung !== null
          ? t('änderungen:mitBeteiligung', { value: payload.beteiligung })
          : '';

        preProcessedPayload = { ...preProcessedPayload, beteiligung };
        break;
      }
    }

    const richTextComponents = {
      referenz: <Anchor href={getLinkFromReferenz(payload?.ref)}/>,
    };

    return r(`änderungen:liste.person.${type}`, richTextComponents, { payload: preProcessedPayload });
  };

  const translateFunc = variant === 'unternehmen' ? translateUnternehmensItem : translatePersonenItem;

  const fakeContent = (
    <dl className={css.container}>
      <div
        className={css.row}
      >
        <dt>{t('formats:date', { value: '2024-12-12' })}</dt>
        <dd>{'Es hat sich etwas geändert aber dieser Text ist nur Demo.'}</dd>
      </div>
      <div
        className={css.row}
      >
        <dt>{t('formats:date', { value: '2024-12-12' })}</dt>
        <dd>{'Es hat sich etwas geändert aber dieser Text ist nur Demo.'}</dd>
        <dd>{'Es hat sich etwas geändert aber dieser Text ist nur Demo.'}</dd>
        <dd>{'Es hat sich etwas geändert aber dieser Text ist nur Demo.'}</dd>
      </div>
    </dl>
  );
  const plusTeaser = plusInhalte ? (
    <PlusInformation
      fakeContent={fakeContent}
      plusInhalte={plusInhalte}
    />
  ) : null;

  if (Object.keys(items).length > 0) 
  {
    return (<>
      <dl className={css.container}>
        {
          Object.entries(items).sort((a, b) => b[0].localeCompare(a[0])).map(([date, itemArray], dateIndex) => (
            <div
              id={indices.includes(dateIndex) ? date.substring(0, 4) : undefined}
              key={date + dateIndex}
              className={css.row}
            >
              <dt>{t('formats:date', { value: date })}</dt>
              {itemArray.map((item: any, itemIndex: number) => (
                <dd key={date + '_item_' + itemIndex}>{translateFunc(item)}</dd>
              ))}
            </div>
          ))}
      </dl>
      {plusTeaser}
    </>);
  }

  return (
    <>
      <div className={css.noResult}>{t('profiles:filter.noResults')}</div>
      {plusTeaser}
    </>);
};

export default ZeitlicherVerlauf;
